import { Injectable } from '@angular/core';
import * as Rx from 'rxjs';
import { UserInfoService } from '../user/user-info.service';
import { ZipCodeAPIService } from './location-api.service';

export type TNewLocationType = "zip" | "city" | "";
export type TNewLocationStyle = "opportunity" | "skillsgap";

export interface INewLocation {
	type: TNewLocationType,
	radius: number,
	loc: string
}

export interface ILocationInfo {
	Name: string,
	Abbr: string,
	ZipCode: string,
	Radius: number,
	Lat: string,
	Lng: string
}

@Injectable({
	providedIn: 'root'
})
export class LocationInfoService {
	private accountMonitor: Rx.Subscription;
	private defaultRadius: number = 50;
	private defaultLoc: ILocationInfo = {
		Name: 'California',
		Abbr: 'CA',
		ZipCode: '92123',
		Radius: this.defaultRadius,
		Lat: "",
		Lng: ""
	};

	constructor(
		private userInfo: UserInfoService,
		private zipCodeAPI: ZipCodeAPIService
	) {
		// bind function to allow getCurrentPosition calling back
		this.convertGeoToZipCode = this.convertGeoToZipCode.bind(this);
		this.cleanupGeo = this.cleanupGeo.bind(this);
		this.getZipCodeByIP();
	
		this.accountMonitor = this.userInfo.UserInfoTimestamp.subscribe({
			next: () => {
				this.setUserZipCode();
			},
			error: (err: any) => {
				console.log(err);
			}
		});
	}

	ngOnDestroy() {
		if (this.accountMonitor) {
			this.accountMonitor.unsubscribe();
		}
	}	

	//#region zip code by user
	private get zipCodeUser(): ILocationInfo {
		return JSON.parse(sessionStorage.getItem('zipCodeUser'));
	}
	private set zipCodeUser(v: ILocationInfo) {
		if (v == null) {
			sessionStorage.removeItem("zipCodeUser");
		}
		else {
			sessionStorage.setItem('zipCodeUser', JSON.stringify(v));
		}
	}

	private setUserZipCode(): void {
		if (this.userInfo.isAnonymous()) {
			this.zipCodeUser = null;
			return;
		}
		let zipCode = this.userInfo.getProfile().ZipCode.trim();
		if (zipCode === "") {
			this.zipCodeUser = null;
			return;
		}
		this.zipCodeAPI.getLocation(zipCode).subscribe({
			next: (response:any) => {
				this.zipCodeUser = {
					Name: response.StateName,
					Abbr: response.StateCode,
					ZipCode: zipCode,
					Radius: this.defaultRadius,
					Lat: response.Latitude != null? response.Latitude.toString() : "",
					Lng: response.Longitude != null? response.Longitude.toString() : "",
				};
			},
			error: (err:any) => {
				console.log(err);
				this.zipCodeUser = {
					Name: "",
					Abbr: "",
					ZipCode: zipCode,
					Radius: this.defaultRadius,
					Lat: "",
					Lng: ""
				};
			}
		});
	}
	//#endregion zip code by user

	//#region get zip code by geo
	private get zipCodeGeo(): ILocationInfo {
		return JSON.parse(localStorage.getItem('zipCodeGeo'));
	}
	private set zipCodeGeo(v: ILocationInfo) {
		if (v == null) {
			localStorage.removeItem("zipCodeGeo");
		}
		else {
			localStorage.setItem('zipCodeGeo', JSON.stringify(v));
		}
	}
	
	private getGoogleZipCodeByGeo(geo: any) {
		let geocoder = new google.maps.Geocoder;
		let latlng = { lat: parseFloat(geo.coords.latitude), lng: parseFloat(geo.coords.longitude) };
		geocoder.geocode({ 'location': latlng }, (results, status) => {
			if (status === 'OK') {
				try {
					if (results[0]) {
						var addresses = results[0].address_components;
						var name = '', abbr = '', zip = '';
						for (var i = 0; i < addresses.length; i++) {
							if (addresses[i].types[0] === 'administrative_area_level_1') {
								name = addresses[i].long_name;
								abbr = addresses[i].short_name;
							}
							else if (addresses[i].types[0] === 'postal_code') {
								zip = addresses[i].short_name;
							}
						}
						if (name !== '' && abbr !== '' && zip !== null) {
							this.zipCodeGeo = {
								Name: name,
								Abbr: abbr,
								ZipCode: zip,
								Radius: this.defaultRadius,
								Lat: geo.coords.latitude,
								Lng: geo.coords.longitude
							};
						}
						else {
							this.zipCodeGeo = null;
						}
					}
				}
				catch (ex) {
					console.log("Fail to get GEO" + ex);
					this.zipCodeGeo = null;
				}
			}
			else {
				console.log('Geocoder failed due to: ' + status);
				this.zipCodeGeo = null;
			}
		});
	}

	private convertGeoToZipCode(geo: any) {
		if (this.zipCodeGeo != null && this.zipCodeGeo.Lat === geo.coords.latitude && this.zipCodeGeo.Lng === geo.coords.longitude) {
			return;
		}
		this.getGoogleZipCodeByGeo(geo);
	}

	private cleanupGeo(error: any) {
		switch (error.code) {
			case error.PERMISSION_DENIED:
				console.log("Location: User denied the request for Geolocation.");
				break;
			case error.POSITION_UNAVAILABLE:
				console.log("Location: information is unavailable.");
				break;
			case error.TIMEOUT:
				console.log("Location: The request to get user location timed out.");
				break;
			case error.UNKNOWN_ERROR:
				console.log("Location: An unknown error occurred.");
				break;
		}
		this.zipCodeGeo = null;
	}

	getZipCodeByGeo() {
		if (window.location.protocol.toLowerCase().indexOf("https") == 0) {
			if (navigator.geolocation) {
				navigator.geolocation.getCurrentPosition(this.convertGeoToZipCode, this.cleanupGeo);
			}
		}
	};
	//#endregion get zip code by geo

	//#region  zip code by ip
	private get zipCodeIp(): ILocationInfo {
		return JSON.parse(localStorage.getItem('zipCodeIp'));
	}
	private set zipCodeIp(v: ILocationInfo) {
		if (v == null) {
			localStorage.removeItem("zipCodeIp");
		}
		else {
			localStorage.setItem('zipCodeIp', JSON.stringify(v));
		}
	}

	private getZipCodeByIP() {
		this.zipCodeAPI.getLocationByIP().subscribe({
			next: (response) => {
				if (response != null) {
					this.zipCodeIp = {
						Name: response.StateName,
						Abbr: response.StateCode,
						ZipCode: response.ZipCode,
						Radius: this.defaultRadius,
						Lat: response.Latitude.toString(),
						Lng: response.Longitude.toString()
					};
				}
			},
			error: (err) => {
				console.log(err);
				this.zipCodeIp = null;
			}
		});
	}
	//#endregion zip code by ip

	get(): ILocationInfo {
		if (this.zipCodeUser != null) {
			return JSON.parse(JSON.stringify(this.zipCodeUser));
		}
		else if (this.zipCodeGeo != null) {
			return JSON.parse(JSON.stringify(this.zipCodeGeo));
		}
		else if (this.zipCodeIp != null) {
			return JSON.parse(JSON.stringify(this.zipCodeIp));
		}
		else {
			return JSON.parse(JSON.stringify(this.defaultLoc));
		}
	}
}


